import { NumberInput } from "@carbon/react";
import get from "lodash.get";
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  UseControllerProps,
  useFormState,
} from "react-hook-form";

export type NumberInputFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  id: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  control: Control<TFieldValues>;
  name: TName;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
} & Pick<UseControllerProps<TFieldValues, TName>, "rules">;

export function NumberInputFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  id,
  name,
  label,
  placeholder,
  control,
  helperText,
  min,
  max,
  step,
  disabled,
  rules,
}: NumberInputFormFieldProps<TFieldValues, TName>) {
  const { errors } = useFormState({ control });
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange } }) => (
        <NumberInput
          id={id}
          label={label}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          invalidText={typeof error?.message === "string" ? error.message : null}
          invalid={!!error?.message}
          helperText={helperText}
          disabled={disabled}
          value={value}
          onChange={(e, state) => {
            if (e.target instanceof HTMLInputElement) {
              onChange(e.target.value);
            } else {
              onChange(String(state.value));
            }
          }}
        />
      )}
    />
  );
}
